import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Globe2 } from "lucide-react";
import Footer from "@/components/site/Footer";

/**
 * BlogLayout — Shared shell for blog articles (FR & EN).
 *
 * Props :
 *  - kicker : short label above the H1 ("Guide" / "Insights")
 *  - title : article H1
 *  - lede : short intro paragraph rendered below the H1
 *  - datePublishedIso : ISO date for the visible byline
 *  - dateLabel : localized displayed date ("11 septembre 2026")
 *  - readingMinutes : estimated reading time (int)
 *  - hreflangSwitcher : { label, to } — link to the other language version
 *  - backLabel / backTo : navigation link
 *  - cta : { label, to }
 *  - children : article body (JSX)
 *
 * The layout owns : logo header, hero, article container (max-w-3xl for readability),
 * CTA block, footer.
 */
export default function BlogLayout({
  kicker,
  title,
  lede,
  datePublishedIso,
  dateLabel,
  readingMinutes,
  hreflangSwitcher,
  backLabel = "Retour à l’accueil",
  backTo = "/",
  cta,
  children,
  testId = "blog-article",
}) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid={testId}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="blog-logo">
            <img
              src="/brand/logo-em-group.png"
              alt="EM Group — Agence digitale"
              width="40"
              height="40"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="h-10 w-10 object-contain"
            />
            <span className="font-outfit tracking-tight text-lg">
              EM <span className="text-white/50">Group</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            {hreflangSwitcher && (
              <Link
                to={hreflangSwitcher.to}
                data-testid="blog-hreflang-switch"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors border border-white/10 rounded-full px-3 py-1.5"
              >
                <Globe2 size={13} /> {hreflangSwitcher.label}
              </Link>
            )}
            <Link
              to={backTo}
              data-testid="blog-back"
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft size={16} /> {backLabel}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(229,36,49,0.15),transparent_55%),radial-gradient(ellipse_at_70%_70%,rgba(31,44,110,0.18),transparent_55%)]" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-10">
          {kicker && (
            <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
              — {kicker}
            </span>
          )}
          <h1 className="mt-4 font-outfit text-3xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
            {title}
          </h1>
          {lede && (
            <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed">
              {lede}
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-white/45 uppercase tracking-[0.14em] font-medium">
            {dateLabel && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={12} />
                <time dateTime={datePublishedIso}>{dateLabel}</time>
              </span>
            )}
            {readingMinutes && (
              <span>· {readingMinutes} min</span>
            )}
            {hreflangSwitcher && (
              <Link
                to={hreflangSwitcher.to}
                className="sm:hidden inline-flex items-center gap-1 text-white/60 hover:text-white transition"
              >
                <Globe2 size={12} /> {hreflangSwitcher.label}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Article body */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <article className="space-y-6 text-white/80 font-light leading-[1.75] text-[15px] md:text-[16px]">
            {children}
          </article>

          {/* CTA */}
          {cta && (
            <div className="mt-14 md:mt-16 rounded-3xl border border-white/10 bg-gradient-to-br from-[#E52431]/12 via-white/[0.02] to-transparent p-8 md:p-10" data-testid="blog-cta-block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431] font-semibold">
                — {cta.eyebrow || "On en discute ?"}
              </div>
              <h3 className="mt-3 font-outfit text-2xl md:text-3xl font-light tracking-tight">
                {cta.title || "Prêt à passer à l’action ?"}
              </h3>
              {cta.subtitle && (
                <p className="mt-3 text-white/65 text-sm md:text-base leading-relaxed max-w-xl">
                  {cta.subtitle}
                </p>
              )}
              <Link
                to={cta.to}
                data-testid="blog-cta-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E52431] to-[#FF6A75] text-white font-semibold px-6 py-3.5 text-xs uppercase tracking-[0.14em] hover:shadow-[0_0_36px_rgba(229,36,49,0.45)] transition"
              >
                {cta.label}
                <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {/* Bottom navigation */}
          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs">
            <div className="text-white/45 uppercase tracking-[0.14em]">
              EM Group · Agence digitale
            </div>
            <div className="flex gap-5 text-white/50">
              <Link to="/mentions-legales" className="hover:text-white transition-colors">
                Mentions légales
              </Link>
              <Link to="/confidentialite" className="hover:text-white transition-colors">
                Confidentialité
              </Link>
              <Link to="/cgv" className="hover:text-white transition-colors">
                CGV
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
